import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button, LocationCard } from '@/components';
import { getBusinessUnitLabel } from '@/types/common';
import {
  VehicleAlert,
  VehicleAlertStatus,
  getVehicleAlertById,
  getAlertTypeLabel,
} from '@/services/api/monitoring';
import MonitoringComments from './components/MonitoringComments';
import {
  formatDateTime,
  formatDuration,
  formatContextEntry,
  humanizeKey,
  getStaleAgingMinutes,
  getLastTelemetryAt,
  getThresholdMinutes,
} from './monitoringFormat';

const StatusPill: React.FC<{ status: VehicleAlertStatus }> = ({ status }) => {
  const resolved = status === VehicleAlertStatus.RESOLVED;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-medium ${
        resolved
          ? 'bg-[#E8F8F0] text-[#1FC16B]'
          : 'bg-[#FEF3F2] text-[#F04438]'
      }`}
    >
      <i
        className={`${
          resolved ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
        } text-[14px]`}
      ></i>
      {resolved ? 'Resolved' : 'Open'}
    </span>
  );
};

const StatCard: React.FC<{
  icon: string;
  label: string;
  value: React.ReactNode;
  accent?: string;
}> = ({ icon, label, value, accent = 'text-gray-400' }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4">
    <div className="mb-2 flex items-center gap-2">
      <i className={`${icon} text-[16px] ${accent}`}></i>
      <span className="text-[12px] font-medium uppercase tracking-wide text-gray-500">
        {label}
      </span>
    </div>
    <p className="text-[15px] font-semibold text-gray-900">{value}</p>
  </div>
);

const MonitoringDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const passedAlert = (location.state as { alert?: VehicleAlert } | null)
    ?.alert;

  const [alert, setAlert] = useState<VehicleAlert | null>(passedAlert ?? null);
  const [isLoading, setIsLoading] = useState(!passedAlert);

  const fetchAlert = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await getVehicleAlertById(id);
      setAlert(data ?? null);
    } catch (error) {
      console.error('Failed to load alert:', error);
      setAlert(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!passedAlert) {
      fetchAlert();
    }
  }, [passedAlert, fetchAlert]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-primary"></div>
          <p className="mt-4 font-medium text-gray-600">
            Loading alert details...
          </p>
        </div>
      </div>
    );
  }

  if (!alert) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="mx-auto max-w-md text-center">
          <h1 className="mb-2 text-2xl font-bold text-gray-900">
            Alert Not Found
          </h1>
          <p className="mb-6 text-gray-600">
            The alert "{id}" could not be found.
          </p>
          <Button onClick={() => navigate('/monitoring')} variant="primary">
            &larr; Back to Monitoring
          </Button>
        </div>
      </div>
    );
  }

  // Keys already surfaced elsewhere on the page (stat cards, vehicle card,
  // location card, status pill) — hidden here to avoid duplication.
  const HIDDEN_CONTEXT_KEYS = new Set([
    'ageMinutes',
    'thresholdMinutes',
    'lastTelemetryAt',
    'latitude',
    'longitude',
    'platNomor',
    'businessUnit',
    'status',
  ]);
  const contextEntries = alert.context
    ? Object.entries(alert.context).filter(
        ([key]) => !HIDDEN_CONTEXT_KEYS.has(key)
      )
    : [];

  return (
    <div className="mx-auto max-w-full bg-gray-50 px-6 py-5">
      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate('/monitoring')}
            className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            aria-label="Back"
          >
            <i className="ri-arrow-left-line text-[18px]"></i>
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              {getAlertTypeLabel(alert.alertType)}
            </h1>
            <p className="mt-0.5 text-[13px] text-gray-500">
              {alert.platNomor || 'Unknown vehicle'} ·{' '}
              {getBusinessUnitLabel(alert.businessUnit)}
            </p>
          </div>
        </div>
        <StatusPill status={alert.status} />
      </div>

      {/* Content */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
        {/* Left: alert info */}
        <div className="flex w-full flex-col gap-4 lg:basis-[58%]">
          {/* Stat cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              icon="ri-wifi-off-line"
              label="Offline For"
              accent="text-[#F04438]"
              value={(() => {
                const aging = getStaleAgingMinutes(alert);
                return aging === null ? '-' : formatDuration(aging);
              })()}
            />
            <StatCard
              icon="ri-alarm-warning-line"
              label="Threshold"
              value={(() => {
                const threshold = getThresholdMinutes(alert);
                return threshold === null ? '-' : formatDuration(threshold);
              })()}
            />
            <StatCard
              icon="ri-time-line"
              label="Open For"
              accent="text-[#F5B01D]"
              value={formatDuration(alert.durationMinutes)}
            />
            <StatCard
              icon="ri-base-station-line"
              label="Last Telemetry"
              value={formatDateTime(getLastTelemetryAt(alert))}
            />
            <StatCard
              icon="ri-fire-line"
              label="Fired At"
              value={formatDateTime(alert.firedAt)}
            />
            <StatCard
              icon="ri-checkbox-circle-line"
              label="Resolved At"
              accent="text-[#1FC16B]"
              value={
                alert.resolvedAt ? (
                  formatDateTime(alert.resolvedAt)
                ) : (
                  <span className="text-[#F5B01D]">Ongoing</span>
                )
              }
            />
          </div>

          {/* Vehicle */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h2 className="mb-3 text-[15px] font-semibold text-gray-900">
              Vehicle
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[12px] text-gray-500">Plat Nomor</p>
                <p className="text-[14px] font-medium text-gray-900">
                  {alert.platNomor || '-'}
                </p>
              </div>
              <div>
                <p className="text-[12px] text-gray-500">Business Unit</p>
                <p className="text-[14px] font-medium text-gray-900">
                  {getBusinessUnitLabel(alert.businessUnit)}
                </p>
              </div>
              <div>
                <p className="text-[12px] text-gray-500">OEM</p>
                <p className="text-[14px] font-medium text-gray-900">
                  {alert.oemName || '-'}
                </p>
              </div>
              <div>
                <p className="text-[12px] text-gray-500">Model</p>
                <p className="text-[14px] font-medium text-gray-900">
                  {alert.modelName || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Last known location */}
          <LocationCard
            latitude={alert.latitude}
            longitude={alert.longitude}
            emptyMessage="No location data for this alert."
          />

          {/* Context */}
          {contextEntries.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h2 className="mb-3 text-[15px] font-semibold text-gray-900">
                Context
              </h2>
              <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                {contextEntries.map(([key, value]) => (
                  <div key={key} className="min-w-0">
                    <p className="text-[12px] text-gray-500">
                      {humanizeKey(key)}
                    </p>
                    <p className="break-words text-[14px] font-medium text-gray-900">
                      {formatContextEntry(key, value)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audit footer */}
          <p className="px-1 text-[12px] text-gray-400">
            Created by {alert.createdBy || '-'} on{' '}
            {formatDateTime(alert.createdAt)}
            {alert.updatedAt
              ? ` · Updated by ${alert.updatedBy || '-'} on ${formatDateTime(
                  alert.updatedAt
                )}`
              : ''}
          </p>
        </div>

        {/* Right: comments */}
        <div className="w-full lg:basis-[42%]">
          <MonitoringComments alertID={alert.ID} />
        </div>
      </div>
    </div>
  );
};

export default MonitoringDetailPage;
